import { workflowStartIntent, type WorkflowStartIntent } from "./intent-model.ts";
import { useWorkflowQuery } from "./react.tsx";
import {
  ArrowsClockwiseIcon,
  Bell,
  CaretDownIcon,
  CaretRightIcon,
  CheckCircle,
  Clock,
  FlowArrow,
  ListBullets,
  Play,
  XCircle
} from "@phosphor-icons/react";
import {
  Fragment,
  type ReactNode,
  useEffect,
  useRef,
  useMemo,
  useState
} from "react";
import { CreateWorkflowInstanceDialog } from "./CreateWorkflowInstanceDialog";
import {
  type WorkflowDefinitionRecord,
  type WorkflowInstanceRecord,
  type WorkflowSourceStep,
} from "../../api/workflows/model.ts";

interface WorkflowExplorerProps {
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
  onOpenWorkflow: (
    definition: WorkflowDefinitionRecord,
    instance?: WorkflowInstanceRecord
  ) => void;
  onOpenInstances: (definition: WorkflowDefinitionRecord) => void;
  onOpenInstance: (
    definition: WorkflowDefinitionRecord,
    instance: WorkflowInstanceRecord
  ) => void;
}



function StepIcon({ step }: { step: WorkflowSourceStep }) {
  if (step.kind === "waitForEvent") return <Bell size={13} />;
  if (step.kind === "sleep" || step.kind === "sleepUntil") {
    return <Clock size={13} />;
  }
  return <FlowArrow size={13} />;
}

function InstanceIcon({ instance }: { instance: WorkflowInstanceRecord }) {
  if (instance.status === "complete") {
    return <CheckCircle size={13} weight="fill" className="text-status-success" />;
  }
  if (instance.status === "errored" || instance.status === "terminated") {
    return <XCircle size={13} weight="fill" className="text-status-error" />;
  }
  return (
    <ArrowsClockwiseIcon
      size={13}
      className={(instance.status === "queued" || instance.status === "running" || instance.status === "waiting" || instance.status === "rollingBack" || instance.status === "waitingForPause") ? "animate-spin" : ""}
    />
  );
}

function TreeAction({
  label,
  onClick,
  children,
  disabled = false
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="flex h-5 w-5 cursor-pointer items-center justify-center rounded bg-transparent text-kumo-subtle hover:bg-kumo-fill hover:text-kumo-default disabled:cursor-default disabled:opacity-35"
    >
      {children}
    </button>
  );
}

function TreeRow({
  depth,
  label,
  detail,
  icon,
  expanded,
  expandable = false,
  onClick,
  onDoubleClick,
  actions
}: {
  depth: number;
  label: string;
  detail?: string;
  icon: React.ReactNode;
  expanded?: boolean;
  expandable?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  actions?: React.ReactNode;
}) {
  return (
    <div
      role="treeitem"
      aria-expanded={expandable ? expanded : undefined}
      className="group flex min-h-6 w-full items-center pr-1 text-xs text-kumo-subtle hover:bg-kumo-elevated/60 hover:text-kumo-default"
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent py-[3px] text-left"
        style={{ paddingLeft: 6 + depth * 12 }}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        <span className="flex h-3 w-3 shrink-0 items-center justify-center">
          {expandable ? (
            expanded ? (
              <CaretDownIcon size={10} weight="bold" />
            ) : (
              <CaretRightIcon size={10} weight="bold" />
            )
          ) : null}
        </span>
        <span className="flex h-[15px] w-[15px] shrink-0 items-center justify-center">
          {icon}
        </span>
        <span className="truncate text-kumo-default">{label}</span>
        {detail ? (
          <span className="truncate text-[10px] text-kumo-subtle">{detail}</span>
        ) : null}
      </button>
      {actions ? (
        <div className="flex shrink-0 items-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export function WorkflowExplorer({
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange,
  onOpenWorkflow,
  onOpenInstances,
  onOpenInstance
}: WorkflowExplorerProps) {
  const workspaceRef = useRef(workspaceId);workspaceRef.current = workspaceId;
  const query = useWorkflowQuery({ view: "explorer", workspaceId, active: !collapsed, ...(refreshToken === undefined ? {} : { refreshToken }) });
  const snapshot = query.snapshot.catalog, loading = query.snapshot.loading, error = query.snapshot.error;
  const refresh = () => query.dispatch({ _tag: "Refresh" });
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["connection"]));
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [triggerIntent, setTriggerIntent] = useState<WorkflowStartIntent | null>(null);
  const triggerTarget = triggerIntent?.workspaceId === workspaceId ? triggerIntent : null;
  useEffect(() => { setExpanded(new Set(["connection"]));setSelectedClass(null); }, [workspaceId, refreshToken]);
  useEffect(() => { setTriggerIntent(null); }, [workspaceId]);

  function toggle(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function run(definition: WorkflowDefinitionRecord) {
    if (!workspaceId) return;
    setTriggerIntent(workflowStartIntent(workspaceId, definition));
  }

  const selectedDefinition = snapshot.definitions.find(
    (definition) => definition.className === selectedClass
  );

  return (
    <section
      className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line"
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            {collapsed ? (
              <CaretRightIcon size={10} weight="bold" />
            ) : (
              <CaretDownIcon size={10} weight="bold" />
            )}
            Workflows
          </button>
        )}
        <TreeAction
          label="Run workflow"
          disabled={!selectedDefinition}
          onClick={() => {
            if (selectedDefinition) run(selectedDefinition);
          }}
        >
          <Play size={13} weight="fill" />
        </TreeAction>
        <TreeAction label="Refresh workflows" onClick={() => void refresh()}>
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </TreeAction>
      </div>

      {!collapsed ? (
        <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
          {workspaceId ? (
            snapshot.definitions.length ? (
              <>
                <TreeRow
                  depth={0}
                  label={connectionName || "Workerspace"}
                  detail="connected"
                  icon={<FlowArrow size={14} />}
                  expandable
                  expanded={expanded.has("connection")}
                  onClick={() => toggle("connection")}
                />
                {expanded.has("connection")
                  ? snapshot.definitions.map((definition) => {
                      const classKey = `class:${definition.className}`;
                      const stepsKey = `${classKey}:steps`;
                      const instancesKey = `${classKey}:instances`;
                      const instances = snapshot.instances.filter(
                        (instance) =>
                          instance.className === definition.className
                      );
                      return (
                        <Fragment key={definition.className}>
                          <TreeRow
                            depth={1}
                            label={definition.className}
                            detail={definition.bindingNames.join(", ") || "unbound"}
                            icon={<FlowArrow size={14} weight="bold" />}
                            expandable
                            expanded={expanded.has(classKey)}
                            onClick={() => {
                              setSelectedClass(definition.className);
                              toggle(classKey);
                            }}
                            onDoubleClick={() => onOpenWorkflow(definition)}
                            actions={
                              <>
                                <TreeAction
                                  label={`Browse ${definition.className} instances`}
                                  onClick={() => onOpenInstances(definition)}
                                >
                                  <ListBullets size={12} />
                                </TreeAction>
                                <TreeAction
                                  label={`Run ${definition.className}`}
                                  onClick={() => run(definition)}
                                >
                                  <Play size={12} weight="fill" />
                                </TreeAction>
                              </>
                            }
                          />
                          {expanded.has(classKey) ? (
                            <>
                              <TreeRow
                                depth={2}
                                label="Steps"
                                detail={String(definition.steps.length)}
                                icon={<FlowArrow size={13} />}
                                expandable
                                expanded={expanded.has(stepsKey)}
                                onClick={() => toggle(stepsKey)}
                              />
                              {expanded.has(stepsKey)
                                ? definition.steps.map((step) => (
                                    <TreeRow
                                      key={step.id}
                                      depth={3}
                                      label={step.name}
                                      detail={`${step.kind} · line ${step.line}`}
                                      icon={<StepIcon step={step} />}
                                    />
                                  ))
                                : null}
                              <TreeRow
                                depth={2}
                                label="Instances"
                                detail={String(instances.length)}
                                icon={<ArrowsClockwiseIcon size={13} />}
                                expandable
                                expanded={expanded.has(instancesKey)}
                                onClick={() => toggle(instancesKey)}
                                actions={
                                  <TreeAction
                                    label={`Open ${definition.className} instance list`}
                                    onClick={() => onOpenInstances(definition)}
                                  >
                                    <ListBullets size={12} />
                                  </TreeAction>
                                }
                              />
                              {expanded.has(instancesKey)
                                ? instances.map((instance) => (
                                    <TreeRow
                                      key={instance.id}
                                      depth={3}
                                      label={instance.id}
                                      detail={instance.status}
                                      icon={<InstanceIcon instance={instance} />}
                                      onClick={() =>
                                        onOpenInstance(definition, instance)
                                      }
                                      onDoubleClick={() =>
                                        onOpenWorkflow(definition, instance)
                                      }
                                    />
                                  ))
                                : null}
                            </>
                          ) : null}
                        </Fragment>
                      );
                    })
                  : null}
              </>
            ) : (
              <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
                Export a class that extends WorkflowEntrypoint, declare a
                workflow binding, then run the Worker and refresh.
              </div>
            )
          ) : (
            <p className="p-2 text-[11px] text-kumo-subtle">
              Open a workerspace to browse Workflows.
            </p>
          )}
          {error ? (
            <div className="m-2 rounded bg-red-500/10 p-2 text-[11px] text-status-error">
              {error}
            </div>
          ) : null}
        </div>
      ) : null}

      {workspaceId && triggerTarget ? (
        <CreateWorkflowInstanceDialog
          workspaceId={triggerTarget.workspaceId}
          className={triggerTarget.className}
          expectedWorkerId={triggerTarget.expectedWorkerId}
          open
          onOpenChange={(open) => {
            if (!open) setTriggerIntent(null);
          }}
          onCreated={(instance) => {
            const requestedWorkspace = triggerTarget.workspaceId;
            if (workspaceRef.current !== requestedWorkspace) return;
            const definition = triggerTarget.definition;
            setTriggerIntent(null);
            void refresh();
            onOpenInstance(definition, instance);
          }}
        />
      ) : null}
    </section>
  );
}
